<script setup lang="ts">
/**
 * The bottom nav on every learner page that is not the player (job #46, Tom
 * 2026-09-24: "ONE consistent bottom nav bar on every screen and in every
 * mode"). This is not a second bar: it mounts the player's own BottomNav and
 * only answers its taps, because off the player there is no player to answer
 * them. App.vue renders it on any route whose meta carries `learnerNav`.
 *
 *   Library, Settings             → go to the player carrying the intent
 *                                   (nav/playerIntent), which does the rest
 *   Listening, Script             → their own addresses, /listening and
 *                                   /script (nav/modeRoute, job #345);
 *                                   Speaking is the player with no intent
 *   centre button                 → back one level (nav/learnerParent, job #48)
 *   Talk to humans, Study groups  → those pages, as in the player
 *
 * It also renders the one "leave the call?" sheet (nav/leaveGuard), because a
 * bar that is reachable during a call must never end one by being tapped.
 */
import { computed, inject, onMounted, onUnmounted, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import BottomNav from './BottomNav.vue'
import { requestPlayerIntent, type PlayerIntent } from '../nav/playerIntent'
import { learnerParent } from '../nav/learnerParent'
import { modePath } from '../nav/modeRoute'
import { leaveAsk, answerLeave } from '../nav/leaveGuard'
import { hasScriptSystem, hasCharacterScript } from '../script/scriptOffered'
import { welshScriptFor } from '../script/welsh/welshScript'
import { useI18n } from '../composables/useI18n'
import { useSocialRowsGate } from '../composables/useSocialDoor'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

interface AuthLike { isAuthenticated?: Ref<boolean>; isGuest?: Ref<boolean> }
interface CourseLike { course_code?: string; target_lang?: string }
const auth = inject<AuthLike | null>('auth', null)
const activeCourse = inject<Ref<CourseLike | null> | null>('activeCourse', null)

// The same gate PlayerContainer puts on the Talk and Study groups rows —
// signed in, not a guest, not a school account (job #113).
const showConnect = useSocialRowsGate(computed(() => !!auth?.isAuthenticated?.value && !auth?.isGuest?.value))
const courseCode = computed(() => activeCourse?.value?.course_code || '')
// The same test LearningPlayer's hasScriptPractice makes, from the same course.
const hasScriptPractice = computed(() => {
  const lang = activeCourse?.value?.target_lang || courseCode.value.split('_')[0]
  return !!lang && (hasScriptSystem(lang) || hasCharacterScript(lang) || welshScriptFor(lang, courseCode.value))
})

// The centre button is the back arrow here, and back is one level up (job
// #48): from a group to your groups, from games night to Talk to humans, and
// to learning only from a top-level page. nav/learnerParent holds the map.
function goBack(): void {
  const to = learnerParent(route.name)
  if (to === '/') toPlayer(null)
  else void router.push(to)
}

// Which mode row the selector lights off the player (Tom, 2026-09-24: one row
// lit, the mode you are in). Talk to humans and everything reached through it,
// the groups and everything inside them; any other learner page lights
// nothing, so Speaking reads as the way back to the course.
const TALK_PAGES = new Set(['talk-to-humans', 'connect', 'connect-call', 'games-night'])
const GROUP_PAGES = new Set(['study-groups', 'study-group', 'study-group-invite'])
const pageMode = computed(() => {
  const name = typeof route.name === 'string' ? route.name : ''
  if (TALK_PAGES.has(name)) return 'talk'
  if (GROUP_PAGES.has(name)) return 'studyGroups'
  return ''
})

function toPlayer(intent: PlayerIntent | null): void {
  requestPlayerIntent(intent)
  void router.push('/')
}

const leaveTitle = computed(() =>
  leaveAsk.value?.kind === 'games' ? t('nav.leaveGamesTitle') : t('nav.leaveCallTitle'))
const leaveBody = computed(() =>
  leaveAsk.value?.kind === 'games' ? t('nav.leaveGamesBody') : t('nav.leaveCallBody'))

// Pages clear the floating bar by this much; the social pages' shared
// .page-scroll and the /me pages read it (style.css). Unset = no bar.
onMounted(() => document.documentElement.style.setProperty('--learner-nav-space', 'calc(72px + var(--shell-nav-clearance, 12px) + 12px)'))
onUnmounted(() => {
  document.documentElement.style.removeProperty('--learner-nav-space')
  answerLeave(false)
})
</script>

<template>
  <BottomNav
    currentScreen="page"
    :courseCode="courseCode"
    :hasScriptPractice="hasScriptPractice"
    :showConnect="showConnect"
    :pageMode="pageMode"
    @navigate="(id: string) => id === 'library' && toPlayer('library')"
    @openSettings="toPlayer('settings')"
    @startLearning="goBack"
    @togglePlayback="goBack"
    @toggleListening="router.push(modePath('listening'))"
    @toggleScriptPractice="router.push(modePath('script'))"
    @speaking="toPlayer(null)"
    @talk="router.push('/talk')"
    @studyGroups="router.push('/groups')"
  />

  <div v-if="leaveAsk" class="leave-scrim" data-test="leave-call-confirm" @click.self="answerLeave(false)">
    <div class="leave-sheet" role="alertdialog" aria-modal="true" :aria-label="leaveTitle">
      <p class="leave-title">{{ leaveTitle }}</p>
      <p class="leave-body">{{ leaveBody }}</p>
      <button class="leave-btn stay" data-test="leave-call-stay" @click="answerLeave(false)">{{ t('nav.leaveStay') }}</button>
      <button class="leave-btn go" data-test="leave-call-go" @click="answerLeave(true)">{{ t('nav.leaveGo') }}</button>
    </div>
  </div>
</template>

<style scoped>
.leave-scrim {
  position: fixed;
  inset: 0;
  z-index: 3100; /* over the bar (3000), which is what raised the question */
  background: rgba(20, 16, 12, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.leave-sheet {
  width: 100%;
  max-width: 640px;
  background: #fff;
  color: var(--ink-primary, #2C2622);
  border-radius: 16px 16px 0 0;
  padding: 20px;
  padding-bottom: calc(20px + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.leave-title { font-size: 18px; font-weight: 600; margin: 0; }
.leave-body { font-size: 15px; color: var(--ink-secondary, #6B635C); margin: 0 0 6px; }
.leave-btn {
  min-height: 48px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: 1.5px solid var(--ink-primary, #2C2622);
  cursor: pointer;
}
.leave-btn.stay { background: var(--ink-primary, #2C2622); color: #fff; }
.leave-btn.go { background: transparent; color: #b3261e; border-color: #b3261e; }
</style>
